import React, { useCallback, useMemo, useState } from 'react';
import { Skeleton } from 'antd';
import { useTheme } from 'styled-components';
import { I18n } from '@aws-amplify/core';

import { DEFAULT_TIMELINE_THEME, Timeline as VideoTimeline } from '@/components/ui/Timeline';
import { formatTime } from '@/components/ui/Timeline/utils/time-conversions';
import { useBeraReportV2 } from '@/context/BeraReportV2';

import { Header } from './Header';
import { StretchHint } from './StretchHint';
import { Zoom } from './Zoom';
import { List } from './List';
import { findStretchIdsPastVideo } from './helpers';
import {
	CARD_BACKGROUND,
	Container,
	Divider,
	RulerLabels,
	RulerSkeleton,
	TimelineTrack,
	TimelineViewport,
	VideoEndMarker,
	VideoEndMarkerLabel
} from './styles';

import type { TimelineThemeType } from '@/components/ui/Timeline';

const INITIAL_ZOOM = 1;
const RULER_START_TIME = 0;
const UNZOOMED_TRACK_WIDTH_IN_PERCENT = 100;
const NO_HIGHLIGHTED_STRETCHES: string[] = [];

const STRETCH_COLOR = '#17A93B';
const STRETCH_BACKGROUND = 'rgba(183, 228, 194, 0.4)';
const STRETCH_HOVER_BACKGROUND = 'rgba(108, 202, 131, 0.6)';
const OUT_OF_RANGE_BACKGROUND = 'rgba(191, 191, 191, 0.4)';
const OUT_OF_RANGE_SELECTED_BACKGROUND = 'rgba(140, 140, 140, 0.5)';

interface TimelineProps {
	currentTime: number;
	onSeek: (time: number) => void;
}

export function Timeline({ currentTime, onSeek }: Readonly<TimelineProps>): JSX.Element {
	const {
		allReports,
		videoDuration,
		rulerDuration,
		outOfRangeStretchIds,
		listedStretches,
		rulerStretches,
		isEditMode,
		isVideoUnavailable,
		isDraggingStretch,
		editableStretchIds,
		hoveredTaskId,
		onApplyRulerChange,
		onStretchDragChange,
		onSelectReport,
		onSelectStretches,
		onClearSelection,
		onHoverTask
	} = useBeraReportV2();

	const theme = useTheme();

	const [zoom, setZoom] = useState<number>(INITIAL_ZOOM);

	const rulerTheme = useMemo<TimelineThemeType>(
		() => ({
			...DEFAULT_TIMELINE_THEME,
			colors: {
				...DEFAULT_TIMELINE_THEME.colors,
				background: CARD_BACKGROUND,
				identifiedCycle: {
					background: STRETCH_BACKGROUND,
					border: STRETCH_COLOR,
					handles: STRETCH_COLOR,
					hoverBackground: STRETCH_HOVER_BACKGROUND
				},
				outOfRangeCycle: {
					background: OUT_OF_RANGE_BACKGROUND,
					selectedBackground: OUT_OF_RANGE_SELECTED_BACKGROUND,
					border: theme.palette.secondary[150],
					handles: theme.palette.secondary[250]
				}
			}
		}),
		[theme]
	);

	const trackWidthInPercent = zoom * UNZOOMED_TRACK_WIDTH_IN_PERCENT;

	const hasStretches = !!listedStretches.length;

	const hasEditableStretch = !!editableStretchIds.length;

	const hasOutOfRangeStretch = !!outOfRangeStretchIds.length;

	const videoEndRatio = videoDuration / rulerDuration;

	let pastVideoStretchIds: string[] = [];

	if (hasOutOfRangeStretch) {
		pastVideoStretchIds = findStretchIdsPastVideo({ stretches: rulerStretches, videoDuration });
	}

	let outOfRangeFromTime: number | undefined;

	if (hasOutOfRangeStretch) {
		outOfRangeFromTime = videoDuration;
	}

	const handleHoverCycle = useCallback(
		(cycleId: string | null): void => {
			const hovered = rulerStretches.find((stretch) => stretch.id === cycleId);

			onHoverTask(hovered?.type_id ?? null);
		},
		[rulerStretches, onHoverTask]
	);

	const hoveredReport = allReports.find((report) => report.task_id === hoveredTaskId);

	const hoveredStretches = rulerStretches.filter((stretch) => stretch.type_id === hoveredTaskId);

	const isHoverShown = !!hoveredReport && !isEditMode && !isDraggingStretch;

	let highlightedStretchIds = NO_HIGHLIGHTED_STRETCHES;

	if (isHoverShown) {
		highlightedStretchIds = hoveredStretches.map((stretch) => stretch.id);
	}

	function handleSelectCycle(cycleId: string | null): void {
		if (isEditMode) {
			return;
		}

		if (!cycleId) {
			onClearSelection();
			return;
		}

		onSelectStretches([cycleId]);

		const clicked = rulerStretches.find((stretch) => stretch.id === cycleId);
		const report = allReports.find((item) => item.task_id === clicked?.type_id);

		if (report) {
			onSelectReport(report);
		}
	}

	if (isVideoUnavailable) {
		return (
			<Container>
				<Header />
				<RulerSkeleton>
					<Skeleton active title={false} paragraph={{ rows: 2 }} />
				</RulerSkeleton>
			</Container>
		);
	}

	return (
		<Container>
			<Header />
			<TimelineViewport>
				<TimelineTrack widthInPercent={trackWidthInPercent}>
					<VideoTimeline
						value={rulerStretches}
						allCycles={rulerStretches}
						onChange={onApplyRulerChange}
						videoDuration={rulerDuration}
						outOfRangeCycleIds={pastVideoStretchIds}
						outOfRangeFromTime={outOfRangeFromTime}
						currentTime={currentTime}
						isEditMode={isEditMode || hasEditableStretch}
						selectedCycleIds={editableStretchIds}
						onCycleSelect={handleSelectCycle}
						theme={rulerTheme}
						onSeek={onSeek}
						onCycleDragChange={onStretchDragChange}
						onCycleHover={handleHoverCycle}
						highlightedCycleIds={highlightedStretchIds}
					/>
					{isHoverShown &&
						hoveredStretches.map((stretch) => (
							<StretchHint
								key={stretch.id}
								tlidName={hoveredReport.task_name}
								stretch={stretch}
								rulerDuration={rulerDuration}
							/>
						))}
					{hasOutOfRangeStretch && (
						<VideoEndMarker ratio={videoEndRatio}>
							<VideoEndMarkerLabel>{I18n.get('End of current video')}</VideoEndMarkerLabel>
						</VideoEndMarker>
					)}
				</TimelineTrack>
				<RulerLabels>
					<span>{formatTime(RULER_START_TIME)}</span>
					<span>{formatTime(rulerDuration)}</span>
				</RulerLabels>
			</TimelineViewport>
			<Zoom zoom={zoom} onChange={setZoom} />
			{hasStretches && <Divider />}
			{hasStretches && <List />}
		</Container>
	);
}
